import React, { useState } from 'react';
import QuestionTable from './QuestionTable';
import { computeSectionTotals } from './templateSchema';

/**
 * A collapsible card representing one section of the paper.
 * Delegates the question list management to QuestionTable so add/edit/delete/
 * +OR behave identically to the direct (section-less) area.
 *
 * Props:
 *   section, index, visibleColumns, options,
 *   onChange(updatedSection), onDelete()
 */
function SectionCard({ section, index, visibleColumns, options, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const [showMeta, setShowMeta] = useState(false);
  const totals = computeSectionTotals(section);

  const setField = (field, value) => onChange({ ...section, [field]: value });

  return (
    <div className="qp-section-card">
      <div className="qp-section-head">
        <div className="qp-section-title">
          <span className="qp-section-badge">
            {section.label || String.fromCharCode(65 + index)}
          </span>
          <input
            type="text"
            className="qp-section-name-input"
            value={section.name || ''}
            onChange={(e) => setField('name', e.target.value)}
            placeholder={`Section ${section.label || String.fromCharCode(65 + index)}`}
          />
          <span className="qp-section-meta">
            · {totals.setMarks}/{totals.attemptMarks} marks
            {section.attemptRule?.type === 'any' && (
              <>
                {' '}· {section.attemptRule.count || 0}/
                {section.attemptRule.total || totals.questionCount} attempt
              </>
            )}
          </span>
        </div>
        <div className="qp-section-actions">
          <button
            type="button"
            className="qp-icon-btn"
            title="Section settings"
            onClick={() => setShowMeta((v) => !v)}
          >
            ✎
          </button>
          <button
            type="button"
            className="qp-icon-btn qp-icon-danger"
            title="Delete section"
            onClick={onDelete}
          >
            🗑
          </button>
          <button
            type="button"
            className="qp-icon-btn"
            title={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </div>
      </div>

      {showMeta && (
        <div className="qp-section-meta-panel">
          <div className="qp-meta-row">
            <div className="qp-meta-field">
              <label>Label</label>
              <input
                type="text"
                value={section.label || ''}
                onChange={(e) => setField('label', e.target.value)}
                maxLength={6}
              />
            </div>
            <div className="qp-meta-field">
              <label>Attempt Rule</label>
              <select
                value={section.attemptRule?.type || 'all'}
                onChange={(e) =>
                  setField('attemptRule', {
                    ...(section.attemptRule || {}),
                    type: e.target.value,
                  })
                }
              >
                <option value="all">Answer all questions</option>
                <option value="any">Answer any N of M</option>
              </select>
            </div>
            {section.attemptRule?.type === 'any' && (
              <>
                <div className="qp-meta-field qp-meta-short">
                  <label>Any</label>
                  <input
                    type="number"
                    min="1"
                    value={section.attemptRule?.count || 0}
                    onChange={(e) =>
                      setField('attemptRule', {
                        ...section.attemptRule,
                        count: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="qp-meta-field qp-meta-short">
                  <label>of</label>
                  <input
                    type="number"
                    min="1"
                    value={section.attemptRule?.total || 0}
                    onChange={(e) =>
                      setField('attemptRule', {
                        ...section.attemptRule,
                        total: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className="qp-meta-row">
            <div className="qp-meta-field" style={{ flex: 1 }}>
              <label>Instructions</label>
              <input
                type="text"
                value={section.instructions || ''}
                onChange={(e) => setField('instructions', e.target.value)}
                placeholder="e.g., Answer all questions"
              />
            </div>
          </div>
        </div>
      )}

      {expanded && (
        <QuestionTable
          questions={section.questions || []}
          visibleColumns={visibleColumns}
          options={options}
          onChange={(qs) => onChange({ ...section, questions: qs })}
          addLabel={`+ Add Question to ${section.name || `Section ${section.label}`}`}
          emptyText="No questions yet. Click below to add one."
        />
      )}
    </div>
  );
}

export default SectionCard;
